import { appendBountyUtmParams, makePixelParams } from '@bounty/constants'
import { useAnalytics } from '@bounty/creators-design-system'
import { Linking } from 'react-native'
import { useAuth } from '../../../hooks'
import { useShopDetailContext } from './ShopDetailContext'
import AsyncStorage from '@react-native-async-storage/async-storage'

const isSafariSafe = () => {
  // eslint-disable-next-line no-restricted-globals
  if (typeof window === 'undefined') {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  }

  return false
}

/**
 * When someone presses something on the storefront that could redirect use this hook
 */
export const useStorefrontPress = () => {
  const [{ isAuthed, isInitializing, user }] = useAuth()
  const { track, identify } = useAnalytics()
  const [
    {
      eventParams,
      redirectUrl: fallbackRedirectUrl,
      productId: fallbackProductId,
    },
    setShopDetailContext,
  ] = useShopDetailContext()

  const redirectToUrl = async ({
    redirectUrl,
    productId,
    userId,
    isNewUser,
    pixelParams,
  }: {
    redirectUrl?: string
    productId?: string
    userId?: string
    isNewUser: boolean
    pixelParams: Partial<Parameters<typeof makePixelParams>[0]>
  }) => {
    const targetRedirectUrl = redirectUrl || fallbackRedirectUrl
    const targetProductId = productId || fallbackProductId

    if (userId) {
      identify(userId)
    }

    const maybeUserId = await AsyncStorage.getItem('analyticsUserId')
    // Make sure this is the same event name as the detail screen directs for logged in users!
    track('Storefront Redirect to Store', {
      ...eventParams,
      ref: maybeUserId,
      isNewUser,
    })

    // Linking is universal, but Safari breaks if there is an async request before redirecting. However,
    // window.location.assign appears to be fair game.
    if (isSafariSafe()) {
      // eslint-disable-next-line no-restricted-globals
      window.location.assign(targetRedirectUrl)
    } else {
      Linking.canOpenURL(targetRedirectUrl).then(() =>
        Linking.openURL(
          appendBountyUtmParams(
            targetRedirectUrl,
            makePixelParams({
              userId,
              productId: targetProductId,
              ...pixelParams,
            }),
          ),
        ),
      )
    }
  }

  const onStorefrontPress = async ({
    redirectUrl,
    productId,
    pixelParams,
  }: {
    /** Overrides the redirect url on useShopDetailContext */
    redirectUrl?: string
    productId?: string
    pixelParams?: Partial<Parameters<typeof makePixelParams>[0]>
  }) => {
    const targetRedirectUrl = redirectUrl || fallbackRedirectUrl

    const maybeUserId = await AsyncStorage.getItem('analyticsUserId')
    if (maybeUserId) {
      redirectToUrl({
        redirectUrl: targetRedirectUrl,
        isNewUser: false,
        userId: maybeUserId,
        productId,
        pixelParams: {
          location: 'creators',
          ...pixelParams,
        },
      })

      return
    }

    if (isInitializing === false && isAuthed && user) {
      redirectToUrl({
        redirectUrl: targetRedirectUrl,
        isNewUser: false,
        userId: user.userId,
        productId,
        pixelParams: { location: 'creators', ...pixelParams },
      })
    } else {
      setShopDetailContext((x) => ({
        ...x,
        signUpScreenOpen: true,
        redirectUrl: targetRedirectUrl,
      }))
    }
  }

  return {
    onStorefrontPress,
    redirectToUrl,
  }
}
